import { getAssetsDates, getAssetsDatetimes } from "../../api";
import { isEmpty, isNil } from "ramda";

let globalAbortController = null;

export const loadDateOptions = async ({
  date,
  date2,
  times,
  asset,
  equipment,
  channel,
  assetEquipments,
  selectedChannels,
  selectedSubs,
  dateOptions,
  setDateOptions = () => {},
  setTimeOptions = () => {},
  setDate = () => {},
  setTime = () => {},
  setDateTimes = () => {},
  setDate2 = () => {},
  setLoading = () => {},
  setChannel,
  firstTime,
  setFirstTime,
  selectedEquipment,
  setSelectedEquipment,
  setLoadingDates,
  setNoData,
}) => {
  if (globalAbortController) {
    globalAbortController.abort();
  }
  // Crear nuevo AbortController para esta ejecución
  globalAbortController = new AbortController();
  const signal = globalAbortController.signal;

  setLoadingDates(true);
  setNoData(true);
  if (!isNil(asset) && !isNil(equipment)) {
    let dates = [];
    let datesDividedByEquipmentsAndChannels = [];
    let auxFirstTime = firstTime;
    for (let indexEquipment = 0; indexEquipment < assetEquipments.length; indexEquipment++) {
      datesDividedByEquipmentsAndChannels.push([]);
      if (selectedEquipment === assetEquipments[indexEquipment] || auxFirstTime) {
        for (let indexSub = 0; indexSub < selectedSubs.length; indexSub++) {
          const channels = selectedEquipment.channels;
          const actualSub = selectedSubs[indexSub];

          let indexChannel = -1;

          for (let i = 0; i < channels.length; i++) {
            const subs = channels[i].substructures;

            if (subs?.some((s) => s === actualSub.ref)) {
              indexChannel = i;
              break;
            }
          }
          const response = await getAssetsDates([asset], equipment, indexChannel + 1, actualSub.ref);
          dates.push(...response.data.data);
          dates.sort((a, b) => new Date(a) - new Date(b));
          const toDivide = response.data.data;
          toDivide.sort((a, b) => new Date(a) - new Date(b));
          datesDividedByEquipmentsAndChannels[indexEquipment][indexSub] = [...new Set(toDivide)];
          if (!isEmpty(dates)) {
            auxFirstTime = false;
            setFirstTime(false);
            setSelectedEquipment(assetEquipments[indexEquipment]);
          }
        }
      }
    }

    const newDates = [...new Set(dates)];

    if (!isEmpty(newDates)) {
      const dateTimeDef = [];

      newDates.sort((a, b) => new Date(a) - new Date(b));
      const promises = [];

      for (let indexEquipment = 0; indexEquipment < datesDividedByEquipmentsAndChannels.length; indexEquipment++) {
        if (!isEmpty(datesDividedByEquipmentsAndChannels[indexEquipment])) {
          for (let indexSub = 0; indexSub < selectedSubs.length; indexSub++) {
            if (!isEmpty(datesDividedByEquipmentsAndChannels[indexEquipment][indexSub])) {
              for (
                let indexDate = 0;
                indexDate < datesDividedByEquipmentsAndChannels[indexEquipment][indexSub].length;
                indexDate++
              ) {
                const date = datesDividedByEquipmentsAndChannels[indexEquipment][indexSub][indexDate];
                const equipmentID = assetEquipments[indexEquipment].equipmentID;
                const channels = selectedEquipment.channels;
                const actualSub = selectedSubs[indexSub];

                let indexChannel = -1;

                for (let i = 0; i < channels.length; i++) {
                  const subs = channels[i].substructures;

                  if (subs?.some((s) => s === actualSub.ref)) {
                    indexChannel = i;
                    break;
                  }
                }
                const channel = indexChannel + 1;

                promises.push(
                  getAssetsDatetimes([asset], equipmentID, channel, actualSub.ref, date, {
                    signal,
                  })
                    .then((timeResponse) => {
                      const timeList = timeResponse.data.data.reverse();
                      const times = timeList.map((time) => ({
                        dateTime: `${date}T${time}`,
                        channel: channel,
                        equipment_ID: equipmentID,
                        asset: asset,
                        substructure: actualSub.name,
                      }));
                      dateTimeDef.push(...times);
                    })
                    .catch((error) => {
                      if (error.name !== "AbortError") {
                        console.error("Error en la llamada:", error);
                      }
                    }),
                );
              }
            }
          }
        }
      }

      try {
        await Promise.all(promises);
        dateTimeDef.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        if (!signal.aborted) {
          setDateTimes(dateTimeDef);
          setDate(0);
          setDate2(newDates.length - 1);
          setDateOptions(newDates);
          setNoData(false);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error en el procesamiento de datos:", error);
        setLoading(false);
      }
    } else {
      setDateOptions(null);

      setNoData(true);
    }

    setLoadingDates(false);
  }
};

export const generateCSV = (data, dateTime, measureName, info, intl) => {
  const header = [
    info.asset,
    info.equipment.name,
    info.equipment.channels[info.channel - 1].name,
    info.substructure.name,
    info.dateTime,
  ].join(",");

  const subHeader = ["PK", intl.formatMessage({ id: measureName })].join(",");

  const pkData = data?.xAxis?.data;
  const serialData = data?.series;

  if (!pkData || !serialData || pkData.length !== serialData.length) {
    throw new Error("Datos faltantes o longitudes desiguales en pkData y serialData.");
  }

  const combinedData = pkData.map((pk, index) => {
    const pkValue = Number(pk).toFixed(2);
    const dataValue = Number(serialData[index].data.toFixed(2));
    return `${pkValue},${dataValue}`;
  });

  const csvData = [header, subHeader, ...combinedData].join("\n");

  return csvData;
};

export const generateCSVInc = (data, dateTime, measureName, intl) => {
  const header = [intl.formatMessage({ id: "depth" }), intl.formatMessage({ id: measureName })].join(",");
  const pkData = data?.xAxis?.data;
  const serialData = data?.series;
  const combinedData = serialData?.map((sD, index) => [pkData[index], sD.data]);

  if (!pkData || !serialData || pkData.length !== serialData.length) {
    throw new Error("Datos faltantes o longitudes desiguales en pkData y serialData.");
  }

  const csvData = [header, ...combinedData].join("\n");

  return csvData;
};
